/* covered.ro — Skin Lab: scroll-driven phone transformation.
   Works with the CSS phone (.cv-phone) and with product renders (.cv-lab__device). */
(() => {
  if (customElements.get('cv-skin-lab')) return;

  const clamp = (v, min = 0, max = 1) => Math.min(max, Math.max(min, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);

  // Scroll timeline (fractions of the pinned distance).
  const T = { intro: [0, 0.1], cut: [0.1, 0.34], apply: [0.34, 0.56], finish: [0.56, 0.97] };

  class CvSkinLab extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.cv-lab__track');
      this.stage = this.querySelector('.cv-lab__stage');
      // Element that carries the finish-change sweep (--wipe / --wipe-on).
      this.back = this.querySelector('.cv-lab__device, .cv-phone__back');
      this.skins = Array.from(this.querySelectorAll('[data-skin]'));
      this.steps = Array.from(this.querySelectorAll('.cv-lab__step'));
      this.chips = Array.from(this.querySelectorAll('.cv-lab__chip'));
      this.readout = this.querySelector('[data-readout-value]');
      this.finishLink = this.querySelector('[data-finish-link]');
      this.finishLinkLabel = this.querySelector('[data-finish-link-label]');
      this.finishLinkPrefix = this.finishLinkLabel
        ? this.finishLinkLabel.textContent.replace(this.chips[0] ? this.chips[0].dataset.name : '', '').trim()
        : '';
      this.outline = this.querySelector('.cv-cut--outline');
      this.plateau = this.querySelector('.cv-cut--plateau');
      this.lenses = Array.from(this.querySelectorAll('.cv-cut--lens'));
      this.dot = this.querySelector('.cv-phone__laser-dot');
      const cut = this.querySelector('.cv-phone__cut');
      const box = cut && cut.viewBox && cut.viewBox.baseVal;
      this.viewBox = box && box.width ? { w: box.width, h: box.height } : { w: 780, h: 1634 };
      this.count = this.skins.length;
      this.current = { step: -1, finish: -1 };
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.setup = this.setup.bind(this);

      // Finish rail: fade the edge(s) that hide more chips.
      this.rail = this.querySelector('.cv-lab__finishes');
      this.updateRail = this.updateRail.bind(this);
      if (this.rail) {
        this.rail.addEventListener('scroll', this.updateRail, { passive: true });
        if ('ResizeObserver' in window) {
          this.railObserver = new ResizeObserver(this.updateRail);
          this.railObserver.observe(this.rail);
        }
        this.updateRail();
      }

      this.chips.forEach((chip) => {
        chip.addEventListener('click', () => this.selectFinish(Number(chip.dataset.finish)));
      });
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.skins.indexOf(event.target);
        if (index > -1) this.selectFinish(index, true);
      });
      if (this.reduceMotion.addEventListener) this.reduceMotion.addEventListener('change', this.setup);

      this.setup();
    }

    disconnectedCallback() {
      this.teardown();
      if (this.railObserver) this.railObserver.disconnect();
      if (this.reduceMotion.removeEventListener) this.reduceMotion.removeEventListener('change', this.setup);
    }

    get isStatic() {
      return (
        this.count === 0 ||
        this.reduceMotion.matches ||
        document.body.classList.contains('disable-animations')
      );
    }

    setup() {
      this.teardown();
      this.static = this.isStatic;
      this.classList.toggle('cv-lab--static', this.static);
      if (this.static) {
        this.showStatic(Math.max(0, this.current.finish));
        return;
      }
      this.lengths = {
        outline: this.outline ? this.outline.getTotalLength() : 0,
        plateau: this.plateau ? this.plateau.getTotalLength() : 0,
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.onResize();
    }

    teardown() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      cancelAnimationFrame(this.frame);
      this.ticking = false;
    }

    onResize() {
      this.stickyTop = parseFloat(getComputedStyle(this.stage).top) || 0;
      this.distance = Math.max(1, this.track.offsetHeight - this.stage.offsetHeight);
      this.render();
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      this.frame = requestAnimationFrame(() => {
        this.ticking = false;
        this.render();
      });
    }

    progress() {
      const rect = this.track.getBoundingClientRect();
      return clamp((this.stickyTop - rect.top) / this.distance);
    }

    finishWindow(index) {
      const [start, end] = T.finish;
      const slots = Math.max(1, this.count - 1);
      const slot = (end - start) / slots;
      const from = start + (index - 1) * slot;
      return { from, to: from + slot * 0.7, slot };
    }

    render() {
      const p = this.progress();
      const seg = ([a, b]) => clamp((p - a) / (b - a));
      const intro = easeOut(seg(T.intro));
      const cut = seg(T.cut);
      const apply = easeInOut(seg(T.apply));
      const fin = seg(T.finish);

      // Phone pose
      let ry = lerp(-32, -12, intro);
      ry = lerp(ry, 0, easeInOut(cut));
      const swayTurns = Math.max(1, this.count - 1);
      if (p > T.finish[0]) ry = Math.sin(fin * Math.PI * swayTurns) * 8;
      const press = Math.sin(apply * Math.PI);
      const rx = lerp(8, 2, intro) * (1 - easeInOut(cut)) + press * 3;
      const ty = lerp(48, 0, intro) - (p > T.finish[0] ? Math.sin(fin * Math.PI * 2) * 6 : 0);
      const scale = lerp(0.9, 1, intro) - press * 0.025;

      const style = this.style;
      style.setProperty('--p', p.toFixed(4));
      style.setProperty('--intro', intro.toFixed(4));
      style.setProperty('--cut', cut.toFixed(4));
      style.setProperty('--apply', apply.toFixed(4));
      style.setProperty('--ry', `${ry.toFixed(2)}deg`);
      style.setProperty('--ry-n', (ry / 30).toFixed(3));
      style.setProperty('--rx', `${rx.toFixed(2)}deg`);
      style.setProperty('--ty', `${ty.toFixed(1)}px`);
      style.setProperty('--scale', scale.toFixed(4));

      // Laser trace: outline, then camera plateau, then lenses.
      const outlineT = clamp(cut / 0.62);
      const plateauT = clamp((cut - 0.62) / 0.22);
      const lensT = clamp((cut - 0.84) / 0.16);
      if (this.outline) this.outline.style.strokeDashoffset = 1 - outlineT;
      if (this.plateau) this.plateau.style.strokeDashoffset = 1 - plateauT;
      this.lenses.forEach((lens) => (lens.style.strokeDashoffset = 1 - lensT));

      if (this.dot) {
        let path = null;
        let t = 0;
        if (outlineT > 0 && outlineT < 1) {
          path = this.outline;
          t = outlineT * this.lengths.outline;
        } else if (plateauT > 0 && plateauT < 1) {
          path = this.plateau;
          t = plateauT * this.lengths.plateau;
        }
        if (path) {
          const point = path.getPointAtLength(t);
          style.setProperty('--dot-x', `${((point.x / this.viewBox.w) * 100).toFixed(2)}%`);
          style.setProperty('--dot-y', `${((point.y / this.viewBox.h) * 100).toFixed(2)}%`);
          style.setProperty('--dot-on', '1');
        } else {
          style.setProperty('--dot-on', '0');
        }
      }

      // Skins
      let activeFinish = apply >= 0.5 ? 0 : -1;
      let wipe = 0;
      let wipeOn = 0;
      this.skins.forEach((skin, index) => {
        let t;
        if (index === 0) {
          t = apply;
        } else {
          const w = this.finishWindow(index);
          t = easeInOut(clamp((p - w.from) / (w.to - w.from)));
          if (t >= 0.5) activeFinish = index;
          if (t > 0 && t < 1) {
            wipe = t;
            wipeOn = Math.sin(t * Math.PI);
          }
        }
        skin.style.setProperty('--t', t.toFixed(4));
      });
      if (this.back) {
        this.back.style.setProperty('--wipe', wipe.toFixed(4));
        this.back.style.setProperty('--wipe-on', wipeOn.toFixed(3));
      }

      // Story step
      let step = 0;
      if (p >= T.cut[0]) step = 1;
      if (p >= T.apply[0]) step = 2;
      if (p >= T.finish[0]) step = 3;
      this.updateUi(step, activeFinish);
    }

    updateUi(step, finish) {
      if (step === this.current.step && finish === this.current.finish) return;
      const stepChanged = step !== this.current.step;
      this.current = { step, finish };

      if (stepChanged) {
        this.steps.forEach((el, index) => el.classList.toggle('is-active', index === step));
      }
      this.chips.forEach((chip, index) => {
        const on = index === finish;
        chip.classList.toggle('is-active', on);
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.revealChip(finish);
      this.setReadout(step, finish);
      this.setFinishLink(finish);
    }

    setFinishLink(index) {
      if (!this.finishLink) return;
      const chip = this.chips[Math.max(0, index)];
      const url = chip && chip.dataset.url;
      if (!url) {
        this.finishLink.hidden = true;
        return;
      }
      this.finishLink.hidden = false;
      this.finishLink.href = url;
      if (this.finishLinkLabel) this.finishLinkLabel.textContent = `${this.finishLinkPrefix} ${chip.dataset.name}`.trim();
    }

    updateRail() {
      const rail = this.rail;
      const max = rail.scrollWidth - rail.clientWidth;
      const edges = [];
      if (max > 1) {
        if (rail.scrollLeft > 1) edges.push('start');
        if (rail.scrollLeft < max - 1) edges.push('end');
      }
      if (edges.length) rail.setAttribute('data-overflow', edges.join(' '));
      else rail.removeAttribute('data-overflow');
    }

    revealChip(index) {
      const chip = this.chips[index];
      const rail = chip && chip.parentElement;
      if (!rail || rail.scrollWidth <= rail.clientWidth) return;
      const left = chip.offsetLeft - (rail.clientWidth - chip.offsetWidth) / 2;
      rail.scrollTo({ left: Math.max(0, left), behavior: this.static ? 'auto' : 'smooth' });
    }

    setReadout(step, finish) {
      if (!this.readout) return;
      const stepEl = this.steps[step];
      let text = stepEl ? stepEl.dataset.readout || '' : '';
      if (step === 3 && finish > -1 && this.chips[finish]) {
        text = `${text} ${this.chips[finish].dataset.name}`.trim();
      }
      this.readout.textContent = text;
    }

    selectFinish(index, instant = false) {
      if (index < 0 || index >= this.count) return;
      if (this.static) {
        this.showStatic(index);
        return;
      }
      const target = index === 0 ? T.apply[1] + 0.005 : this.finishWindow(index).to + 0.01;
      const trackTop = this.track.getBoundingClientRect().top + window.scrollY;
      const top = trackTop - this.stickyTop + target * this.distance;
      window.scrollTo({ top, behavior: instant ? 'auto' : 'smooth' });
    }

    showStatic(index) {
      this.skins.forEach((skin, i) => skin.classList.toggle('is-shown', i === index));
      this.steps.forEach((el) => el.classList.add('is-active'));
      this.chips.forEach((chip, i) => {
        chip.classList.toggle('is-active', i === index);
        chip.setAttribute('aria-pressed', i === index ? 'true' : 'false');
      });
      this.current = { step: 3, finish: index };
      this.setReadout(3, index);
      this.setFinishLink(index);
    }
  }

  customElements.define('cv-skin-lab', CvSkinLab);
})();
